import React from 'react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { async_softwareReleasesLaunchCard_AsyncVersionContributors$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesLaunchCard_AsyncVersionContributors.graphql';
import type { async_softwareReleasesVersionContributorsQuery } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesVersionContributorsQuery.graphql';
import { lazy } from '@atlassian/react-loosely-lazy';
import { ContributorsSkeleton } from './contributors-skeleton/index.tsx';
import { Container, type VersionContributors } from './index.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyVersionContributors = lazy<typeof VersionContributors>(() =>
	import(/* webpackChunkName: "async-version-contributors" */ './index').then(
		(module) => module.VersionContributors,
	),
);

export const VersionContributorsSectionSkeleton = () => {
	const { formatMessage } = useIntl();
	return (
		<Container>
			<Text size="UNSAFE_small" weight="bold" color="color.text.subtle" as="p">
				{formatMessage(messages.contributorsLabel)}
			</Text>
			<ContributorsSkeleton contributorCount={4} />
		</Container>
	);
};

type Props = {
	queryRef: PreloadedQuery<async_softwareReleasesVersionContributorsQuery>;
};

export const AsyncVersionContributors = ({ queryRef }: Props) => {
	const { jira } = usePreloadedQuery<async_softwareReleasesVersionContributorsQuery>(
		graphql`
			query async_softwareReleasesVersionContributorsQuery($versionId: ID!) {
				jira @required(action: THROW) {
					version(id: $versionId) @required(action: THROW) {
						...async_softwareReleasesLaunchCard_AsyncVersionContributors
					}
				}
			}
		`,
		queryRef,
	);
	const versionFrag: async_softwareReleasesLaunchCard_AsyncVersionContributors$key = jira.version;

	const version = useFragment(
		graphql`
			fragment async_softwareReleasesLaunchCard_AsyncVersionContributors on JiraVersion {
				contributors @required(action: THROW) {
					edges @required(action: THROW) {
						...versionContributors_softwareReleasesLaunchCard
					}
				}
			}
		`,
		versionFrag,
	);

	return (
		<Placeholder name="version-contributors" fallback={<VersionContributorsSectionSkeleton />}>
			<LazyVersionContributors
				jiraVersionContributors={version.contributors.edges.filter(Boolean)}
			/>
		</Placeholder>
	);
};
